// client/src/components/paymentItems/PaymentItemList.js
import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Alert,
  Snackbar,
  CircularProgress,
  Chip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { AuthContext } from '../../contexts/AuthContext';
import { LocaleContext } from '../../contexts/LocaleContext';
import paymentItemService from '../../services/paymentItemService';

const PaymentItemList = () => {
  const { t, i18n } = useTranslation();
  const { isAdmin } = useContext(AuthContext);
  const { direction } = useContext(LocaleContext);
  const [paymentItems, setPaymentItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  // Current language
  const lang = i18n.language;

  // Function to load payment items
  const loadPaymentItems = async () => {
    try {
      setLoading(true);
      const response = await paymentItemService.getPaymentItems();
      setPaymentItems(response.data);
      setError('');
    } catch (err) {
      setError(t('errorLoadingPaymentItems'));
    } finally {
      setLoading(false);
    }
  };

  // Load payment items on component mount
  useEffect(() => {
    loadPaymentItems();
  }, []);

  // Handle payment item deletion
  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await paymentItemService.deletePaymentItem(itemToDelete.id);
      setSuccess(t('paymentItemDeletedSuccess'));
      loadPaymentItems();
    } catch (err) {
      setError(t('errorDeletingPaymentItem'));
    } finally {
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setItemToDelete(null);
  };

  const handleCloseAlert = () => {
    setSuccess('');
    setError('');
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Typography variant="h4" gutterBottom>
        {t('paymentItems')}
      </Typography>

      {/* Alert messages */}
      <Snackbar 
        open={!!success} 
        autoHideDuration={6000} 
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseAlert} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>

      <Snackbar 
        open={!!error} 
        autoHideDuration={6000} 
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseAlert} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      {/* Add Payment Item button */}
      <Box sx={{ mb: 2, textAlign: direction === 'ltr' ? 'right' : 'left' }}>
        <Button 
          variant="contained" 
          color="primary" 
          startIcon={<AddIcon />}
          component={RouterLink}
          to="/payment-items/create"
        >
          {t('addPaymentItem')}
        </Button>
      </Box>

      {/* Payment Items Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : paymentItems.length === 0 ? (
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="body1">
            {t('noPaymentItemsFound')}
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('name')}</TableCell>
                <TableCell>{t('type')}</TableCell>
                <TableCell>{t('faculty')}</TableCell>
                <TableCell>{t('createdBy')}</TableCell>
                <TableCell align="center">{t('actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paymentItems.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>{item.name}</TableCell>
                  <TableCell>
                    <Chip 
                      label={t(item.item_type)} 
                      color={item.item_type === 'university' ? 'primary' : 'secondary'} 
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {item.faculty 
                      ? item.faculty[`name_${lang}`] 
                      : t('notApplicable')}
                  </TableCell>
                  <TableCell>{item.creator?.username || ''}</TableCell>
                  <TableCell align="center">
                    <IconButton
                      color="primary"
                      component={RouterLink}
                      to={`/payment-items/edit/${item.id}`}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      color="error"
                      onClick={() => handleDeleteClick(item)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
      >
        <DialogTitle>{t('confirmDelete')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('deletePaymentItemConfirmation', { 
              name: itemToDelete?.name 
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>{t('cancel')}</Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            {t('delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PaymentItemList;